import axios from 'axios';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import moment from 'moment';
import path from 'path';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';

import {
	Button,
	DatePicker,
	Dropdown,
	Icon,
	Menu,
	message,
	Modal,
	Select,
	Table,
	Typography,
} from 'antd';

import Fallback from '../Fallback';
import FileDuration from '../FileDuration';
import FileExt from '../FileExt';
import Form from '../Form';
import PageSizeHandler from '../PageSizeHandle';
import PlayCell from '../PlayCell';
import Progress from '../Progress';
import { FiltersContainer, TableActions } from './styles';

import { FiDownload, FiPlus, FiPlusCircle, FiTrash2, FiUpload, FiXCircle } from 'react-icons/fi';
import env from '../../config/env';
import { resolveFileSrc } from '../../helpers/fileSrcResolver';
import { useDownload, useElement, useFilesValidator, usePlayer } from '../../hooks';
import InterviewsAPI from '../../services/sdks/interviews';
import FilesUploader from '../FilesUploader';

const LocalInterviews = ({ user, program }) => {
	const player = usePlayer();
	const download = useDownload();

	const {
		categorizedTypes: { interviews: categories },
	} = useElement();

	const [fallback, setFallback] = useState({ initialData: false });
	const [interviews, setInterviews] = useState([]);
	const [selectedInterviews, setSelectedInterviews] = useState([]);
	const [visibleModals, setVisibleModals] = useState({ addInterviews: false });
	const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: null });
	const [progress, setProgress] = useState(null);
	const [filters, setFilters] = useState({ category: undefined, releaseDate: null });
	const [isValidating, setIsValidating] = useState(false);
	const { hasValidationError } = useFilesValidator();
	const [newInterviews, setNewInterviews] = useState({
		category: undefined,
		releaseDate: null,
		files: [],
	});

	const columns = [
		{
			title: 'Categoria',
			key: 'category',
			render: (interview) => (
				<PlayCell
					meta={{ primary: `ENTREVISTA REDE - ${interview?.category.split('-')[1]}` }}
					isPlaying={player?.ref === interview?._id && player?.isPlaying}
					onPause={player.resume}
					onPlay={() => {
						player.start({
							src: resolveFileSrc({ fileName: interview.filename, bucket: env.miscS3Bucket }),
							ref: interview?._id,
							meta: {
								name: `ENTREVISTA LOCAL - ${interview?.category.split('-')[1]}`,
								artist: interview?.program?.name,
							},
						});
					}}
				/>
			),
		},
		{
			title: 'Lançamento',
			dataIndex: 'releaseDate',
			key: 'date',
		},
		{
			title: 'Duração',
			align: 'center',
			key: 'duration',
			render: (interview) => (
				<FileDuration
					src={resolveFileSrc({ fileName: interview.filename, bucket: env.miscS3Bucket })}
				/>
			),
		},
		{
			title: 'Formato',
			align: 'center',
			key: 'ext',
			render: (interview) => <FileExt src={interview?.filename} />,
		},
		{
			key: 'id',
			title: 'ID',
			align: 'center',
			render: ({ _id }) => (
				<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
					0,
					10
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Ações',
			align: 'right',
			key: 'options',
			render: (interview) => (
				<>
					<Dropdown
						placement='bottomRight'
						overlay={
							<Menu>
								<Menu.Item
									key='play'
									onClick={() => {
										player.start({
											src: resolveFileSrc({
												fileName: interview.filename,
												bucket: env.miscS3Bucket,
											}),
											ref: interview?._id,
											meta: {
												name: `ENTREVISTA LOCAL - ${interview?.category.split('-')[1]}`,
												artist: interview?.program?.name,
											},
										});
									}}>
									<Icon type='play-circle' /> Reproduzir
								</Menu.Item>
								<Menu.Item key='download' onClick={() => handleDownload({ interview })}>
									<Icon type='download' /> Fazer download
								</Menu.Item>

								<Menu.Divider />

								<Menu.Item
									key='delete'
									className='ant-dropdown-menu-item-danger'
									onClick={() => {
										Modal.confirm({
											title: 'Excluir entrevista?',
											icon: 'exclamation-circle',
											content: 'Essa ação não poderá ser revertida, deseja continuar mesmo assim?',
											okText: 'Excluir',
											onOk: () => handleDelete({ interviewId: interview?._id }),
											okButtonProps: {
												icon: 'delete',
												type: 'danger',
											},
											cancelText: 'Cancelar',
											cancelButtonProps: {
												icon: 'close-circle',
											},
										});
									}}>
									<Icon type='delete' /> Excluir
								</Menu.Item>
							</Menu>
						}>
						<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 16 }} type='more' />
					</Dropdown>
				</>
			),
		},
	];

	const handleDownload = useCallback(
		async ({ interview }) => {
			try {
				const name = `ENTREVISTA LOCAL - ${interview?.category.split('-')[1]}`;

				return download({ filename: interview?.filename, name });
			} catch (error) {
				console.error(error);
				message.error('Não foi possível fazer o download.');
			}
		},
		[download]
	);

	const handleDownloadAsZIP = useCallback(async () => {
		setFallback((prev) => ({ ...prev, multiDownload: true }));

		const zip = new JSZip();

		for (let index = 0; index < selectedInterviews.length; index++) {
			const interview = selectedInterviews[index];
			const filePath = resolveFileSrc({ fileName: interview.filename, bucket: env.miscS3Bucket });
			const blob = await axios.get(filePath, { responseType: 'blob' });
			const name = `ENTREVISTA LOCAL - ${interview?.category.split('-')[1]}`;
			const ext = path.extname(interview?.filename);

			const fName = `${interview?.program?.name} - ${name}${ext}`;

			zip.file(fName, blob.data, { binary: true });
		}

		const zipContent = await zip.generateAsync({ type: 'blob' });

		let downloadName = `ENTREVISTAS LOCAIS - ${program?.name}`;

		filters?.releaseDate && (downloadName = `${downloadName} - ${filters.releaseDate}`);

		saveAs(zipContent, downloadName);
		setFallback((prev) => ({ ...prev, multiDownload: false }));
	}, [program, selectedInterviews, filters]);

	const handleDelete = useCallback(async ({ interviewId }) => {
		try {
			setFallback((prev) => ({ ...prev, deleting: true }));

			await InterviewsAPI.destroy({ interviewId });

			setInterviews((interviews) => interviews.filter(({ _id }) => _id !== interviewId));
		} catch (error) {
			console.error(error);
			message.error('Houve um erro ao deletar a entrevista');
		} finally {
			setFallback((prev) => ({ ...prev, deleting: false }));
		}
	}, []);

	const handleMultiDelete = useCallback(async () => {
		for (let index = 0; index < selectedInterviews.length; index++) {
			const { _id: interviewId } = selectedInterviews[index];
			await handleDelete({ interviewId });
		}

		setSelectedInterviews([]);
	}, [selectedInterviews, handleDelete]);

	const handleCreateInterviews = useCallback(async () => {
		try {
			console.log('AAAAAAAAAAA')
			const validationSchema = Yup.object().shape({
				category: Yup.string().required('Informe a categoria'),
				program: Yup.string().required('Informe o programa'),
				user: Yup.string().required('Usuário inválido'),
				releaseDate: Yup.string().required('Informe a data de lançamento'),
				files: Yup.array().required('Selecione no mínimo 1 arquivo'),
			});

			const { category, releaseDate, files } = newInterviews;
			const data = {
				category,
				releaseDate,
				files,
				isLocal: true,
				user: user?._id,
				program: program?._id,
			};

			console.log(files)

			console.log(files);

			await validationSchema.validate(data);

			if (hasValidationError(files)) {
				return message.warning(
					'Alguns arquivos selecionados estão corrompidos. Por favor, substitua-os.'
				);
			}

			setFallback((prev) => ({ ...prev, uploadingInterviews: true }));

			const payload = new FormData();

			for (const key in data) {
				if (key === 'files') {
					data[key].forEach((file) => payload.append('file', file.data));
				} else {
					payload.append(key, data[key]);
				}
			}

			const {
				data: { interviews: createdInterviews },
			} = await InterviewsAPI.store({
				payload,
				onUploadProgress: ({ total, loaded }) => {
					setProgress(Math.floor((loaded * 100) / total));
				},
			});

			setInterviews((prev) => [
				...prev,
				...createdInterviews.map((i) => ({ ...i, program: { name: program?.name } })),
			]);

			setNewInterviews({ category: undefined, releaseDate: null, files: [] });
			setVisibleModals((prev) => ({ ...prev, addInterviews: false }));

			/** Aguarda 2 segundos para fechar a mensagem de sucesso do Progress */
			await new Promise((resolve) => setTimeout(() => resolve(), 2000));
		} catch (error) {
			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}

			console.error(error);
			message.error('Houve um erro, tente novamente');
		} finally {
			setProgress(null);
			setFallback((prev) => ({ ...prev, uploadingInterviews: false }));
		}
	}, [user, newInterviews, program, hasValidationError]);

	useEffect(() => {
		setPagination((prev) => ({ ...prev, current: 1 }));
	}, [filters]);

	const isUploadButtonDisabled = useMemo(() => {
		return (
			isValidating ||
			!newInterviews.category ||
			!newInterviews.releaseDate ||
			!newInterviews.files.length
		);
	}, [isValidating, newInterviews]);

	useEffect(() => {
		const fetchInterviews = async () => {
			try {
				setFallback((prev) => ({ ...prev, fetchingInterviews: true }));

				let query = `isLocal=true&page=${pagination?.current - 1}&limit=${
					pagination?.pageSize
				}&program=${program?._id}&user=${user?._id}&`;

				filters?.releaseDate && (query = `${query}releaseDate=${filters.releaseDate}&`);
				filters?.category && (query = `${query}category=${filters.category}&`);

				const {
					data: { interviews, total },
				} = await InterviewsAPI.index({ query });

				setInterviews(interviews);
				setPagination((prev) => ({ ...prev, total }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar as entrevistas');
			} finally {
				setFallback((prev) => ({ ...prev, fetchingInterviews: false }));
			}
		};

		fetchInterviews();
	}, [filters, pagination.current, pagination.pageSize, user]); //eslint-disable-line

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<FiltersContainer>
				<div className='filters'>
					<Select
						showSearch
						value={filters.category}
						placeholder='Categoria'
						style={{ width: '200px', marginRight: '12px' }}
						onChange={(category) => setFilters({ ...filters, category })}
						filterOption={(input, { props: { _search } }) => {
							return _search.match(new RegExp(input, 'i'));
						}}>
						<Select.Option key='all' value={undefined} _search='TODOS'>
							TODOS
						</Select.Option>

						{categories.map((category) => (
							<Select.Option value={category} key={category}>
								ENTREVISTAS LOCAIS - {category.split('-')[1]}
							</Select.Option>
						))}
					</Select>

					<DatePicker
						placeholder='Data de lançamento'
						format='DD/MM/yyyy'
						style={{ width: '200px' }}
						value={filters?.releaseDate && moment(filters.releaseDate, 'DD/MM/yyyy')}
						onChange={(_, releaseDate) => setFilters({ ...filters, releaseDate })}
					/>
				</div>

				<Button onClick={() => setVisibleModals({ ...visibleModals, addInterviews: true })}>
					<FiPlus /> Novas entrevistas
				</Button>
			</FiltersContainer>

			<TableActions>
				<div className='actions'>
					<span>
						Quantidade: <strong>{pagination?.total}</strong>
					</span>
					<div>
						<Button
							size='small'
							disabled={!selectedInterviews.length}
							type='ghost'
							onClick={handleDownloadAsZIP}
							loading={fallback?.multiDownload}>
							<FiDownload /> Baixar selecionados{' '}
							{selectedInterviews.length !== 0 && `(${selectedInterviews.length})`}
						</Button>

						<Button
							size='small'
							disabled={!selectedInterviews.length}
							type='danger'
							onClick={() => {
								Modal.confirm({
									title: 'Deletar entrevistas selecionadas?',
									type: 'danger',
									content:
										'Todos as entrevistas selecionados serão excluídos e essa ação não poderá ser revertida, deseja continuar mesmo assim?',
									okText: 'Deletar',
									onOk: handleMultiDelete,
									okButtonProps: {
										icon: 'delete',
										type: 'danger',
									},
									cancelText: 'Cancelar',
									cancelButtonProps: {
										icon: 'close-circle',
									},
								});
							}}>
							<FiTrash2 /> Deletar selecionados{' '}
							{selectedInterviews.length !== 0 && `(${selectedInterviews.length})`}
						</Button>
					</div>
				</div>

				<PageSizeHandler pagination={pagination} setPagination={setPagination} />
			</TableActions>

			<Table
				loading={fallback?.fetchingInterviews}
				size='middle'
				rowKey='_id'
				columns={columns}
				dataSource={interviews}
				style={{ border: 'none' }}
				pagination={{
					...pagination,
					size: 'large',
					onChange: (current) => setPagination({ ...pagination, current }),
				}}
				rowSelection={{
					onChange: (_, selectedRows) => setSelectedInterviews(selectedRows),
				}}
			/>

			<Modal
				width={600}
				visible={visibleModals?.addInterviews}
				onCancel={() => setVisibleModals({ ...visibleModals, addInterviews: false })}
				onOk={handleCreateInterviews}
				cancelButtonProps={{ disabled: fallback?.uploadingInterviews }}
				okButtonProps={{
					loading: fallback?.uploadingInterviews,
					disabled: isUploadButtonDisabled,
				}}
				okText={
					<>
						{!isValidating && <FiUpload />}
						{isValidating ? 'Validando Arquivos' : 'Fazer upload'}
					</>
				}
				cancelText={
					<>
						<FiXCircle /> Cancelar
					</>
				}
				title={
					<>
						<FiPlusCircle /> Adicionar entrevistas
					</>
				}>
				<Form.Container>
					<Form.Item label='Categoria'>
						<Select
							showSearch
							value={newInterviews?.category}
							placeholder='Categoria'
							style={{ marginBottom: 8, width: '100%' }}
							onChange={(category) => setNewInterviews({ ...newInterviews, category })}
							filterOption={(input, { props: { _search } }) => {
								return _search.match(new RegExp(input, 'i'));
							}}>
							{categories.map((category) => (
								<Select.Option key={category} value={category} _search={category}>
									ENTREVISTAS LOCAIS - {category.split('-')[1]}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Data de lançamento'>
						<DatePicker
							placeholder='Data de lançamento'
							format='DD/MM/yyyy'
							style={{ width: '100%' }}
							value={newInterviews?.releaseDate && moment(newInterviews.releaseDate, 'DD/MM/yyyy')}
							onChange={(_, releaseDate) => setNewInterviews({ ...newInterviews, releaseDate })}
						/>
					</Form.Item>

					<FilesUploader
						multiple
						onChange={(files) => setNewInterviews((prev) => ({ ...prev, files }))}
						onStartValidation={() => setIsValidating(true)}
						onEndValidation={() => setIsValidating(false)}
						validTypes={['.mp3', '.wav']}
					/>
				</Form.Container>
			</Modal>

			<Progress
				progress={progress}
				succesTitle='Entrevistas enviadas com sucesso'
				title='Enviando entrevistas, por favor aguarde...'
			/>
		</>
	);
};

export default connect(({ user }) => ({ user }))(LocalInterviews);
